import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

export default function HeroSection() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #f8faff 0%, #f0f4ff 40%, #f5f7fc 100%)' }}
    >
      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(15,23,42,0.03) 100%)' }}
      />

      {/* Subtle grid */}
      <div className="absolute inset-0 grid-pattern opacity-15" />

      {/* ===== Side lighting — left blue glow ===== */}
      <motion.div
        className="absolute pointer-events-none"
        style={{ top: '15%', left: '-5%' }}
        animate={{ x: [0, 18, 0], y: [0, -12, 0], scale: [1, 1.05, 1] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="w-[500px] h-[600px] md:w-[650px] md:h-[750px] rounded-full blur-[160px]"
          style={{ background: 'radial-gradient(circle at center, rgba(79,107,255,0.30) 0%, rgba(79,107,255,0.12) 50%, transparent 75%)' }}
        />
      </motion.div>

      {/* ===== Side lighting — right blue-violet glow ===== */}
      <motion.div
        className="absolute pointer-events-none"
        style={{ bottom: '5%', right: '-5%' }}
        animate={{ x: [0, -15, 0], y: [0, 18, 0], scale: [1, 1.06, 1] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="w-[450px] h-[550px] md:w-[600px] md:h-[700px] rounded-full blur-[160px]"
          style={{ background: 'radial-gradient(circle at center, rgba(123,97,255,0.26) 0%, rgba(79,107,255,0.12) 50%, transparent 75%)' }}
        />
      </motion.div>

      {/* ===== SINGLE focal glow — top-left ===== */}
      <div className="absolute -top-[10%] -left-[8%] w-[500px] h-[500px] md:w-[700px] md:h-[700px] rounded-full pointer-events-none blur-[140px]"
        style={{ background: 'radial-gradient(circle at center, rgba(79,107,255,0.28) 0%, rgba(123,97,255,0.14) 40%, transparent 70%)' }}
      />

      {/* Central white lightening */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        <div className="w-[1000px] h-[600px] md:w-[1400px] md:h-[800px] rounded-full"
          style={{ background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.35) 40%, transparent 70%)' }}
        />
      </div>

      {/* Central radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[54%] pointer-events-none">
        <div className="w-[1200px] h-[700px] rounded-full"
          style={{ background: 'radial-gradient(ellipse at center, rgba(79,107,255,0.14) 0%, rgba(123,97,255,0.06) 35%, transparent 65%)' }}
        />
      </div>
      {/* Headline spotlight */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[62%] pointer-events-none">
        <div className="w-[800px] h-[320px] rounded-full blur-[30px]"
          style={{ background: 'radial-gradient(ellipse at center, rgba(79,107,255,0.15) 0%, rgba(123,97,255,0.07) 45%, transparent 75%)' }}
        />
      </div>

      {/* ===== Orbital system — Top-left ===== */}
      <motion.div
        className="absolute pointer-events-none"
        style={{ top: '-28%', left: '-22%' }}
        animate={{ rotate: [0, 360], x: [0, 15, 0], y: [0, -10, 0] }}
        transition={{ duration: 120, repeat: Infinity, ease: "linear", x: { duration: 11, repeat: Infinity, ease: "easeInOut" }, y: { duration: 10, repeat: Infinity, ease: "easeInOut" } }}
      >
        <svg width="900" height="900" viewBox="0 0 900 900" fill="none" className="w-[600px] h-[600px] md:w-[900px] md:h-[900px]" style={{ opacity: 0.45 }}>
          <defs>
            <linearGradient id="grad-ring-1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(79,107,255,0.30)" />
              <stop offset="100%" stopColor="rgba(123,97,255,0.20)" />
            </linearGradient>
          </defs>
          <circle cx="450" cy="450" r="440" stroke="rgba(79,107,255,0.15)" strokeWidth="1.2" />
          <circle cx="450" cy="450" r="330" stroke="url(#grad-ring-1)" strokeWidth="1.8" />
          <circle cx="450" cy="450" r="220" stroke="rgba(79,107,255,0.12)" strokeWidth="1.2" strokeDasharray="6 14" />
          <circle cx="450" cy="450" r="120" stroke="rgba(123,97,255,0.12)" strokeWidth="1" />
          <circle cx="450" cy="10" r="5" fill="rgba(79,107,255,0.35)">
            <animate attributeName="opacity" values="0.35;0.15;0.35" dur="3s" repeatCount="indefinite" />
          </circle>
          <circle cx="890" cy="450" r="4.5" fill="rgba(123,97,255,0.30)">
            <animate attributeName="opacity" values="0.30;0.12;0.30" dur="4s" repeatCount="indefinite" />
          </circle>
          <circle cx="450" cy="890" r="4" fill="rgba(79,107,255,0.25)">
            <animate attributeName="opacity" values="0.25;0.10;0.25" dur="3.5s" repeatCount="indefinite" />
          </circle>
          <circle cx="10" cy="450" r="4.5" fill="rgba(123,97,255,0.28)">
            <animate attributeName="opacity" values="0.28;0.12;0.28" dur="4.5s" repeatCount="indefinite" />
          </circle>
          <circle cx="780" cy="450" r="4" fill="rgba(79,107,255,0.25)">
            <animate attributeName="opacity" values="0.25;0.10;0.25" dur="3s" repeatCount="indefinite" />
          </circle>
          <circle cx="450" cy="120" r="4" fill="rgba(123,97,255,0.25)">
            <animate attributeName="opacity" values="0.25;0.10;0.25" dur="3.5s" repeatCount="indefinite" />
          </circle>
          <circle cx="120" cy="450" r="3.5" fill="rgba(79,107,255,0.22)">
            <animate attributeName="opacity" values="0.22;0.08;0.22" dur="4s" repeatCount="indefinite" />
          </circle>
          <line x1="450" y1="10" x2="890" y2="450" stroke="rgba(79,107,255,0.07)" strokeWidth="0.6" />
          <line x1="450" y1="10" x2="10" y2="450" stroke="rgba(123,97,255,0.07)" strokeWidth="0.6" />
          <circle cx="450" cy="450" r="6" fill="rgba(79,107,255,0.25)" />
          <circle r="4" fill="rgba(79,107,255,0.50)">
            <animateMotion dur="25s" repeatCount="indefinite" path="M 450 120 A 330 330 0 0 1 780 450 A 330 330 0 0 1 450 780 A 330 330 0 0 1 120 450 A 330 330 0 0 1 450 120" />
            <animate attributeName="opacity" values="0.50;0.25;0.50" dur="3s" repeatCount="indefinite" />
          </circle>
          <circle r="3.5" fill="rgba(123,97,255,0.45)">
            <animateMotion dur="25s" repeatCount="indefinite" begin="-12s" path="M 450 120 A 330 330 0 0 1 780 450 A 330 330 0 0 1 450 780 A 330 330 0 0 1 120 450 A 330 330 0 0 1 450 120" />
            <animate attributeName="opacity" values="0.45;0.20;0.45" dur="4s" repeatCount="indefinite" />
          </circle>
        </svg>
      </motion.div>

      {/* ===== Orbital system — Right side ===== */}
      <motion.div
        className="absolute pointer-events-none hidden md:block"
        style={{ top: '5%', right: '-16%' }}
        animate={{ rotate: [0, -360], x: [0, -12, 0], y: [0, 18, 0] }}
        transition={{ duration: 150, repeat: Infinity, ease: "linear", x: { duration: 12, repeat: Infinity, ease: "easeInOut" }, y: { duration: 10, repeat: Infinity, ease: "easeInOut" } }}
      >
        <svg width="800" height="800" viewBox="0 0 800 800" fill="none" className="w-[500px] h-[500px] md:w-[800px] md:h-[800px]" style={{ opacity: 0.5 }}>
          <defs>
            <linearGradient id="grad-ring-2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(123,97,255,0.25)" />
              <stop offset="100%" stopColor="rgba(79,107,255,0.15)" />
            </linearGradient>
          </defs>
          <circle cx="400" cy="400" r="390" stroke="url(#grad-ring-2)" strokeWidth="1.8" />
          <circle cx="400" cy="400" r="280" stroke="rgba(79,107,255,0.14)" strokeWidth="1.2" />
          <circle cx="400" cy="400" r="170" stroke="rgba(123,97,255,0.12)" strokeWidth="1.2" strokeDasharray="5 12" />
          <circle cx="400" cy="400" r="80" stroke="rgba(79,107,255,0.10)" strokeWidth="1" />
          <circle cx="400" cy="10" r="5" fill="rgba(123,97,255,0.30)">
            <animate attributeName="opacity" values="0.30;0.12;0.30" dur="3s" repeatCount="indefinite" />
          </circle>
          <circle cx="790" cy="400" r="4" fill="rgba(79,107,255,0.28)">
            <animate attributeName="opacity" values="0.28;0.10;0.28" dur="4s" repeatCount="indefinite" />
          </circle>
          <circle cx="400" cy="790" r="4" fill="rgba(123,97,255,0.25)">
            <animate attributeName="opacity" values="0.25;0.10;0.25" dur="3.5s" repeatCount="indefinite" />
          </circle>
          <circle cx="10" cy="400" r="5" fill="rgba(79,107,255,0.30)">
            <animate attributeName="opacity" values="0.30;0.12;0.30" dur="4.5s" repeatCount="indefinite" />
          </circle>
          <circle cx="400" cy="400" r="5" fill="rgba(123,97,255,0.22)" />
          <circle r="3.5" fill="rgba(123,97,255,0.45)">
            <animateMotion dur="30s" repeatCount="indefinite" path="M 400 10 A 390 390 0 0 1 790 400 A 390 390 0 0 1 400 790 A 390 390 0 0 1 10 400 A 390 390 0 0 1 400 10" />
            <animate attributeName="opacity" values="0.45;0.20;0.45" dur="3s" repeatCount="indefinite" />
          </circle>
        </svg>
      </motion.div>

      {/* ===== Small accent cluster — Bottom-left ===== */}
      <motion.div
        className="absolute pointer-events-none"
        style={{ bottom: '-8%', left: '5%' }}
        animate={{ rotate: [0, 360], x: [0, -10, 0], y: [0, -15, 0] }}
        transition={{ duration: 100, repeat: Infinity, ease: "linear", x: { duration: 10, repeat: Infinity, ease: "easeInOut" }, y: { duration: 12, repeat: Infinity, ease: "easeInOut" } }}
      >
        <svg width="400" height="400" viewBox="0 0 400 400" fill="none" className="w-[250px] h-[250px] md:w-[400px] md:h-[400px]">
          <circle cx="200" cy="200" r="190" stroke="rgba(79,107,255,0.14)" strokeWidth="1.2" />
          <circle cx="200" cy="200" r="130" stroke="rgba(123,97,255,0.12)" strokeWidth="1.2" strokeDasharray="4 10" />
          <circle cx="200" cy="200" r="65" stroke="rgba(79,107,255,0.10)" strokeWidth="1" />
          <circle cx="200" cy="10" r="4" fill="rgba(79,107,255,0.28)">
            <animate attributeName="opacity" values="0.28;0.10;0.28" dur="3s" repeatCount="indefinite" />
          </circle>
          <circle cx="390" cy="200" r="3.5" fill="rgba(123,97,255,0.22)">
            <animate attributeName="opacity" values="0.22;0.08;0.22" dur="4s" repeatCount="indefinite" />
          </circle>
          <circle cx="200" cy="200" r="4" fill="rgba(79,107,255,0.20)" />
          <circle r="3" fill="rgba(79,107,255,0.40)">
            <animateMotion dur="20s" repeatCount="indefinite" path="M 200 10 A 190 190 0 0 1 390 200 A 190 190 0 0 1 200 390 A 190 190 0 0 1 10 200 A 190 190 0 0 1 200 10" />
            <animate attributeName="opacity" values="0.40;0.18;0.40" dur="3s" repeatCount="indefinite" />
          </circle>
        </svg>
      </motion.div>

      {/* Button glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 translate-y-[55%] pointer-events-none">
        <div className="w-[360px] h-[100px] rounded-full blur-[35px]"
          style={{ background: 'radial-gradient(ellipse at center, rgba(79,107,255,0.18) 0%, rgba(123,97,255,0.10) 45%, transparent 80%)' }}
        />
      </div>

      {/* ===== CONTENT ===== */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-32 pb-24">
        <motion.h1
          data-testid="hero-headline"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="font-heading text-[3.7rem] sm:text-[5rem] lg:text-[6.5rem] leading-[0.92] font-semibold mb-6"
          style={{ fontWeight: 600, letterSpacing: '-0.4px' }}
        >
          <span className="text-[#08122a]">Nebula </span>
          <span style={{ background: 'linear-gradient(135deg, #4F6BFF 0%, #7A5CFF 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Flow</span>
        </motion.h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="font-body text-xl sm:text-2xl md:text-[28px] text-slate-800 font-medium tracking-tight max-w-none mx-auto mb-6 md:whitespace-nowrap"
        >
          Early-stage capital for regulated and infrastructure-led technology
        </motion.p>

        <motion.p
          data-testid="hero-description"
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="font-body text-base md:text-lg leading-relaxed text-[#475569] max-w-[640px] md:max-w-none mx-auto mb-10"
        >
          <span className="md:hidden">Nebula Flow invests at Seed and Series A across fintech, financial infrastructure, and mission-critical B2B systems. We focus on companies operating in complex and regulated markets where structural discipline creates<br />a durable advantage.</span>
          <span className="hidden md:inline">Nebula Flow invests at Seed and Series A across fintech, financial infrastructure,<br />and mission-critical B2B systems. We focus on companies operating in complex and regulated<br />markets where structural discipline creates a durable advantage.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.75 }}
        >
          <a
            href="#investment-focus"
            data-testid="hero-cta-button"
            className="hero-cta-btn group inline-flex items-center gap-2 px-[18px] py-[10px] text-white text-[14px] font-semibold rounded-full transition-all duration-300 hover:-translate-y-0.5"
          >
            Explore our focus
            <ArrowUpRight className="w-[18px] h-[18px] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
